import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../components/common/Layout';
import ChildAgeBandLabel from '../components/chapters/child/ChildAgeBandLabel';
import NewbornBanner from '../components/chapters/child/NewbornBanner';
import BandTransitionNotice from '../components/chapters/child/BandTransitionNotice';
import MissingBirthYearPrompt from '../components/chapters/child/MissingBirthYearPrompt';
import ChildChapterList from '../components/chapters/child/ChildChapterList';
import { useChildProfile } from '../hooks/useChildProfile';
import { useChildChapters } from '../hooks/useChildChapters';
import { useChildBandEvaluation } from '../hooks/useChildBandEvaluation';
import { isNewborn as checkIsNewborn, getAgeDisplayLabel, calculateAgeBand } from '../utils/childChapterUtils';

/**
 * ChildLifeStory Page
 *
 * Main page for /my-life/child/:memberId
 * Guardian-facing view of a child's life chapters organised by age band.
 */
function ChildLifeStory() {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const {
    profile,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
    create: createProfile,
  } = useChildProfile(memberId);

  const {
    chapters,
    loading: chaptersLoading,
    refetch: refetchChapters,
  } = useChildChapters(memberId);

  const {
    transition,
    evaluate,
    acknowledge,
  } = useChildBandEvaluation(memberId);

  // Track whether we need the birth-year interstitial
  const [needsBirthYear, setNeedsBirthYear] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  // When profile loads or is null, try to auto-generate chapters first.
  // The backend reads date_of_birth from the Person record directly.
  // Only show the birth year prompt if auto-generation fails (no DOB on person).
  useEffect(() => {
    if (profileLoading) return;

    const autoGenerate = async () => {
      if (!profile) {
        // No ChildProfile yet — try to create one (backend uses person's DOB)
        setAutoGenerating(true);
        try {
          await createProfile({});
          await refetchProfile();
          await refetchChapters();
          setNeedsBirthYear(false);
        } catch (err) {
          // If backend returns 400 (no DOB on person), show the birth year prompt
          if (err.response?.status === 400) {
            setNeedsBirthYear(true);
          } else {
            console.error('Auto-generate failed:', err);
            setNeedsBirthYear(true);
          }
        } finally {
          setAutoGenerating(false);
        }
      } else {
        // Profile exists — evaluate band
        setNeedsBirthYear(false);
        evaluate().then(() => refetchChapters());
      }
    };

    autoGenerate();
  }, [profileLoading, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleBirthYearComplete = async (data) => {
    if (profile) {
      // Profile exists but is missing birth year — update it
      await createProfile(data);
    } else {
      // No profile yet — create one
      await createProfile(data);
    }
    setNeedsBirthYear(false);
    await refetchProfile();
    await refetchChapters();
  };

  const handleTransitionDismiss = async () => {
    await acknowledge();
    await refetchProfile();
    await refetchChapters();
  };

  // Derived state
  const childName = profile?.child_name || profile?.person_name || profile?.name || '';
  const birthDate = profile?.birth_date || null;
  const birthYear = profile?.birth_year || null;
  // Get age band from profile or compute from birth info
  const band = profile?.current_age_band || profile?.current_band
    || (birthYear ? calculateAgeBand(birthYear, birthDate) : null);
  const isNewbornChild = checkIsNewborn(birthDate);
  const ageLabel = getAgeDisplayLabel(birthYear, birthDate);

  // Compute days old for newborn banner
  let daysOld = 0;
  if (birthDate) {
    const diffMs = new Date() - new Date(birthDate);
    daysOld = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }

  // Loading state
  if ((profileLoading || autoGenerating) && !profile) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-2/3 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (profileError) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <p className="text-red-600 text-lg">Something went wrong loading this child's profile.</p>
          <button
            onClick={() => refetchProfile()}
            className="mt-4 px-5 py-2 rounded-lg bg-[#556B2F] text-white font-medium hover:bg-[#4A5D23] transition-colors"
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }

  // Birth year interstitial
  if (needsBirthYear) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-[#556B2F] hover:text-[#4A5D23] mb-4 text-sm"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <MissingBirthYearPrompt
            memberId={memberId}
            onComplete={handleBirthYearComplete}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[#556B2F] hover:text-[#4A5D23] mb-4 text-sm"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Age band header */}
        {band && (
          <ChildAgeBandLabel band={band} childName={childName} />
        )}

        {/* Age subtext */}
        {ageLabel && (
          <p className="text-sm text-[#556B2F]/60 -mt-4 mb-6">{ageLabel}</p>
        )}

        {/* Newborn banner */}
        {isNewbornChild && (
          <NewbornBanner daysOld={daysOld} />
        )}

        {/* Band transition notice */}
        {transition && !transition.seenByGuardian && (
          <BandTransitionNotice
            transition={transition}
            onDismiss={handleTransitionDismiss}
          />
        )}

        {/* Chapter list */}
        {chaptersLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl" />
            ))}
          </div>
        ) : (
          <ChildChapterList
            chapters={chapters}
            memberId={memberId}
            isNewborn={isNewbornChild}
            ageBand={band}
          />
        )}
      </div>
    </Layout>
  );
}

export default ChildLifeStory;
